import React, { useEffect, useState, useCallback } from "react";
import Filters from "../components/Filters";
import { ErrorState } from "../components/EmptyState";
import { api, fmtNum, YEARS, monthLabel } from "../lib/api";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, BarChart, Bar } from "recharts";
import { Download } from "lucide-react";

export function RekapBulanan() {
  const [filter, setFilter] = useState({ tahun: "2026", bulan: "september", rw: "", rt: "" });
  const [rekap, setRekap] = useState(null);
  const [rekapRw, setRekapRw] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try { const [a, b] = await Promise.all([api.rekap(filter.tahun, filter.bulan), api.rekapRw(filter.tahun, filter.bulan)]); setRekap(a); setRekapRw(b); }
    catch { setError(true); } finally { setLoading(false); }
  }, [filter.tahun, filter.bulan]);
  useEffect(() => { load(); }, [load]);

  const s = rekap?.summary || {};
  const rows = rekapRw?.data || [];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0e2b26] tracking-tight">Rekap Bulanan</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Periode <strong>{monthLabel(filter.bulan)} {filter.tahun}</strong></p>
      </div>
      <Filters value={filter} onChange={setFilter} onRefresh={load} />
      {error ? <ErrorState onRetry={load} /> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="stat-card"><div className="stat-label">Total Penduduk</div><div className="stat-value">{fmtNum(s.total_penduduk)}</div></div>
            <div className="stat-card"><div className="stat-label">Kelahiran</div><div className="stat-value">{fmtNum(s.kelahiran)}</div></div>
            <div className="stat-card"><div className="stat-label">Kematian</div><div className="stat-value">{fmtNum(s.kematian)}</div></div>
            <div className="stat-card"><div className="stat-label">Laporan Masuk</div><div className="stat-value">{fmtNum(s.jumlah_laporan)}</div></div>
          </div>
          <div className="chart-card">
            <h3 className="font-bold text-[#0e2b26] mb-3">Distribusi Penduduk per RW</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={rows.map((r) => ({ rw: `RW ${r.rw}`, laki: r.laki_laki, perempuan: r.perempuan }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="rw" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip /><Legend />
                <Bar dataKey="laki" name="Laki-laki" fill="#0f766e" radius={[6,6,0,0]} />
                <Bar dataKey="perempuan" name="Perempuan" fill="#f472b6" radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}

export function RekapTahunan() {
  const [tahun, setTahun] = useState("2026");
  const [tren, setTren] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try { setTren(await api.tren(tahun)); } catch { setError(true); } finally { setLoading(false); }
  }, [tahun]);
  useEffect(() => { load(); }, [load]);
  const data = (tren?.data || []).map((t) => ({ ...t, bulan: t.bulan.slice(0,3).toUpperCase() }));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-3 justify-between items-end">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0e2b26] tracking-tight">Rekap Tahunan</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Tren perkembangan penduduk tahun <strong>{tahun}</strong></p>
        </div>
        <div>
          <label className="block text-[0.7rem] font-bold tracking-wider text-[hsl(var(--muted-foreground))] uppercase mb-1.5">Tahun</label>
          <select className="h-11 px-3 rounded-lg border border-[hsl(var(--border))] bg-white text-sm font-medium" value={tahun} onChange={(e)=>setTahun(e.target.value)}>
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>
      {error ? <ErrorState onRetry={load} /> : (
        <>
          <div className="chart-card">
            <h3 className="font-bold text-[#0e2b26] mb-3">Total Penduduk per Bulan</h3>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="bulan" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip /><Legend />
                <Line type="monotone" dataKey="total_penduduk" name="Total" stroke="#0f766e" strokeWidth={2.5} />
                <Line type="monotone" dataKey="laki_laki" name="L" stroke="#3b82f6" strokeWidth={2} />
                <Line type="monotone" dataKey="perempuan" name="P" stroke="#f472b6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="chart-card">
            <h3 className="font-bold text-[#0e2b26] mb-3">Mutasi per Bulan</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="bulan" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip /><Legend />
                <Bar dataKey="kelahiran" fill="#34d399" name="Lahir" />
                <Bar dataKey="kematian" fill="#ef4444" name="Meninggal" />
                <Bar dataKey="pindah" fill="#eab308" name="Pindah" />
                <Bar dataKey="datang" fill="#3b82f6" name="Datang" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}

export function EksporData() {
  const [filter, setFilter] = useState({ tahun: "2026", bulan: "september", rw: "", rt: "" });
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(false);
  const load = useCallback(async () => {
    setError(false);
    try {
      const [rw, st] = await Promise.all([
        api.rekapRw(filter.tahun, filter.bulan),
        api.status(filter.tahun, filter.bulan),
      ]);
      setData(rw); setStatus(st);
    } catch { setError(true); }
  }, [filter.tahun, filter.bulan]);
  useEffect(() => { load(); }, [load]);

  const downloadCsv = () => {
    if (!data?.data?.length) return;
    const headers = ["RW","Jumlah RT","Sudah","Belum","Progress %","Total Penduduk","Laki-laki","Perempuan","Tetap","Sementara"];
    const rows = data.data.map(r => [r.rw, r.jumlah_rt, r.sudah_mengisi, r.belum_mengisi, r.progress, r.total_penduduk, r.laki_laki, r.perempuan, r.penduduk_tetap, r.penduduk_sementara]);
    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `SILAPDI_Rekap_${filter.bulan}_${filter.tahun}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadXlsx = async () => {
    if (!data?.data) return;
    const XLSX = await import("xlsx");
    const wb = XLSX.utils.book_new();

    const rekapSheet = XLSX.utils.aoa_to_sheet([
      ["Rekap RW Kelurahan Suka Asih"],
      [`Periode: ${filter.bulan} ${filter.tahun}`],
      [],
      ["RW", "Jumlah RT", "Sudah", "Belum", "Progress %", "Total Penduduk", "Laki-laki", "Perempuan", "Tetap", "Sementara"],
      ...data.data.map(r => [`RW ${r.rw}`, r.jumlah_rt, r.sudah_mengisi, r.belum_mengisi, r.progress, r.total_penduduk, r.laki_laki, r.perempuan, r.penduduk_tetap, r.penduduk_sementara]),
    ]);
    rekapSheet["!cols"] = [{ wch: 8 }, { wch: 10 }, { wch: 8 }, { wch: 8 }, { wch: 12 }, { wch: 16 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, rekapSheet, "Rekap RW");

    if (status?.data) {
      const statusSheet = XLSX.utils.aoa_to_sheet([
        ["Status Pelaporan"],
        [`Periode: ${filter.bulan} ${filter.tahun}`],
        [`Sudah Mengisi: ${status.sudah_mengisi} / ${status.total_rt} (${status.persentase}%)`],
        [],
        ["RW", "RT", "Status", "Tanggal Submit", "Total Penduduk"],
        ...status.data.map(r => [r.rw, r.rt, r.status, r.tanggal_submit || "-", r.total_penduduk != null ? r.total_penduduk : "-"]),
      ]);
      statusSheet["!cols"] = [{ wch: 6 }, { wch: 6 }, { wch: 18 }, { wch: 22 }, { wch: 14 }];
      XLSX.utils.book_append_sheet(wb, statusSheet, "Status Pelaporan");
    }

    XLSX.writeFile(wb, `SILAPDI_${filter.bulan}_${filter.tahun}.xlsx`);
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0e2b26] tracking-tight">Ekspor Data</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Unduh rekap pelaporan sebagai file Excel (.xlsx), CSV, atau PDF.</p>
      </div>
      <Filters value={filter} onChange={setFilter} onRefresh={load} />
      {error && <ErrorState onRetry={load} />}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <button onClick={downloadXlsx} className="chart-card text-left hover:border-emerald-500 transition" data-testid="export-xlsx">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <Download size={22} />
            </div>
            <div>
              <div className="font-bold text-[#0e2b26]">Ekspor Excel (.xlsx)</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">Rekap RW + Status Pelaporan</div>
            </div>
          </div>
        </button>
        <button onClick={downloadCsv} className="chart-card text-left hover:border-emerald-500 transition" data-testid="export-csv">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-[#e6f3ec] text-[#0e6b4f] flex items-center justify-center">
              <Download size={22} />
            </div>
            <div>
              <div className="font-bold text-[#0e2b26]">Ekspor CSV</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">Rekap RW periode terpilih</div>
            </div>
          </div>
        </button>
        <button onClick={() => window.location.assign("/cetak")} className="chart-card text-left hover:border-emerald-500 transition">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-[#fef3c7] text-[#92400e] flex items-center justify-center">
              <Download size={22} />
            </div>
            <div>
              <div className="font-bold text-[#0e2b26]">Ekspor PDF</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">Lewat halaman Cetak A.5</div>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}

export function Tentang() {
  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0e2b26] tracking-tight">Tentang Sistem</h1>
      </div>
      <div className="bg-white rounded-xl border border-[hsl(var(--border))] p-6 space-y-4">
        <div>
          <div className="text-xs font-bold tracking-widest uppercase text-[#0e6b4f]">SILAPDI</div>
          <div className="text-xl font-extrabold text-[#0e2b26]">Sistem Informasi Laporan Penduduk Digital</div>
          <div className="text-sm text-[hsl(var(--muted-foreground))]">Buku Penduduk A.5 · Kelurahan Suka Asih · Kota Bandung</div>
        </div>
        <p className="text-sm leading-relaxed text-[#334b48]">
          SILAPDI adalah dashboard resmi untuk memantau pelaporan Form A.5 (Buku Perkembangan Penduduk)
          yang disampaikan oleh setiap RT melalui Google Form. Data ditarik langsung dari Google Sheets
          sebagai <strong>sumber kebenaran tunggal</strong>. SILAPDI bersifat <em>read-only</em>: RT tidak
          menginput data melalui aplikasi ini, dan koreksi dilakukan melalui fitur <em>Edit Response</em>
          pada Google Form.
        </p>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg bg-[hsl(var(--muted))] p-3">
            <div className="text-xs text-[hsl(var(--muted-foreground))] font-semibold uppercase">Total RW</div>
            <div className="text-xl font-extrabold text-[#0e2b26]">10</div>
          </div>
          <div className="rounded-lg bg-[hsl(var(--muted))] p-3">
            <div className="text-xs text-[hsl(var(--muted-foreground))] font-semibold uppercase">Total RT</div>
            <div className="text-xl font-extrabold text-[#0e2b26]">75</div>
          </div>
        </div>
        <div className="text-xs text-[hsl(var(--muted-foreground))]">
          Versi 1.0 · Dibangun untuk keperluan administrasi kelurahan.
        </div>
      </div>
    </div>
  );
}
